/**
 * The landing /pricing checkout's stripe/subscribe query builder + allowlist.
 *
 * Kept in its own module (no browser deps) so a unit test can import and assert
 * the emitted key set without pulling in lunar-user.ts's window-dependent code.
 */

/** DEV-12161: the ONLY query keys the landing /pricing checkout sends to
 *  api3/stripe/subscribe. That endpoint hard-400s on any key outside its own
 *  allowlist, and a 400 here means nobody can pay — it has broken production
 *  checkout four times, each time because a key was sent that the backend
 *  rejects:
 *
 *    DEV-11117  `theme`             → "Invalid input parameter: theme"
 *    DEV-10875  `ga_session_id`     → "Invalid input parameter: ga_session_id"
 *    DEV-12158  `internal_user_id`  → "Invalid input parameter: internal_user_id"
 *    DEV-12161  `referral` (bare)   → "Invalid input parameter: referral"
 *
 *  Every key below was verified against the LIVE endpoint by curling it with the
 *  key present and watching the request clear input validation ("Not authorized
 *  (2)" from a fake bearer) instead of a hard-400 naming the key. Evidence
 *  recorded 2026-08-20 (DEV-12161):
 *    accepted → subscription, redirect, mode, promotekit_referral
 *    rejected → `referral` (bare) — the affiliate id MUST be sent as
 *               `promotekit_referral`, the key api3 allowlisted in DEV-11812.
 *
 *  `theme` is ALSO accepted by the live endpoint, but per DEV-12161 the landing
 *  checkout no longer sends it (the reported regression named it, and omitting
 *  an accepted param can never 400); it stays off the request until there is a
 *  product decision to restore themed embeds. Add a key ONLY with the same
 *  live-curl evidence, and say so in the commit. */
export const STRIPE_SUBSCRIBE_ALLOWED_PARAMS = [
  'subscription',
  'redirect',
  'mode',
  'promotekit_referral',
] as const;

/** Build the stripe/subscribe query for the landing /pricing checkout. Split
 *  out from the caller so a unit test can assert the emitted key set against
 *  STRIPE_SUBSCRIBE_ALLOWED_PARAMS — the guard that turns a recurrence into a
 *  failing test instead of a production outage.
 *
 *  `theme` is accepted in the input (callers still pass the site theme) but is
 *  deliberately NOT emitted (DEV-12161). `referral` is mapped to
 *  `promotekit_referral`, the only affiliate key api3 accepts. */
export function buildSubscribeParams(options: {
  subscription: string;
  redirect?: string;
  mode?: string;
  theme?: string;
  referral?: string;
}): Record<string, string | undefined> {
  return {
    subscription: options.subscription,
    redirect: options.redirect,
    mode: options.mode ?? 'url',
    // DEV-10107 / DEV-12161: PromoteKit affiliate id → Stripe metadata. The
    // caller sources it from window.promotekit_referral and passes it as
    // options.referral, but it MUST be sent under `promotekit_referral`; the
    // bare `referral` key hard-400s ("Invalid input parameter: referral",
    // verified by live curl 2026-08-20), which broke checkout for every
    // affiliate-referred user (DEV-12161).
    promotekit_referral: options.referral,
  };
}
